import React from 'react';
import moment from 'moment';
import _ from 'lodash';
import config from '../../config';
import fakeAuth from '../../fakeAuth';
import { formatCurrency, padWithZeros } from '../../helpers';

// Prefactura (estado de cuenta) de una reserva: vista previa imprimible y descargable en PDF.
// Los totales replican exactamente los de la Cuenta Maestra (partials/accounting.js).

const C = {
    ink: '#1f2933',
    muted: '#6b7785',
    line: '#d9dee4',
    soft: '#f3f5f7',
    accent: '#008db5',
    red: '#c0392b',
    green: '#1e8449',
};

const css = `
    .pf-backdrop { background: #525659; min-height: 100vh; padding: 24px 16px 48px; }
    .pf-toolbar { position: sticky; top: 0; z-index: 10; display: flex; gap: 8px; align-items: center; justify-content: center;
        flex-wrap: wrap; max-width: 8.5in; margin: 0 auto 16px; padding: 10px 12px; background: #323639; border-radius: 6px; }
    .pf-toolbar button { border: 0; border-radius: 4px; padding: 7px 14px; font-size: 13px; cursor: pointer; }
    .pf-sheet { background: #fff; color: ${C.ink}; width: 8.5in; min-height: 11in; margin: 0 auto; padding: 0.55in 0.6in;
        box-shadow: 0 4px 18px rgba(0,0,0,.35); font-family: Helvetica, Arial, sans-serif; font-size: 11px; line-height: 1.35; }
    .pf-sheet table { width: 100%; border-collapse: collapse; }
    .pf-sheet th { text-align: left; font-size: 9px; letter-spacing: .06em; text-transform: uppercase; color: ${C.muted};
        border-bottom: 1.5px solid ${C.ink}; padding: 5px 6px; font-weight: 600; }
    .pf-sheet td { padding: 5px 6px; border-bottom: 1px solid ${C.line}; vertical-align: top; }
    .pf-sheet tr { page-break-inside: avoid; }
    .pf-num { text-align: right !important; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .pf-label { font-size: 9px; letter-spacing: .06em; text-transform: uppercase; color: ${C.muted}; margin-bottom: 2px; }
    .pf-section { font-size: 12px; font-weight: 700; margin: 18px 0 6px; color: ${C.ink}; }
    @media print {
        @page { size: letter; margin: 0; }
        body { background: #fff !important; }
        .pf-backdrop { background: #fff; padding: 0; }
        .pf-toolbar { display: none !important; }
        .pf-sheet { box-shadow: none; margin: 0; }
    }
`;

class PrintablePrefactura extends React.Component {

    state = {
        currency: localStorage.getItem('lb.currency') || 'USD',
        IdProperty: localStorage.getItem('lb.ip') ? JSON.parse(localStorage.getItem('lb.ip')).IdProperty : null,
        hotel_name: localStorage.getItem('lb.ip') ? JSON.parse(localStorage.getItem('lb.ip')).Name : null,
        issued: moment(),
        logo: null,
        downloading: false,
    }

    // Lifecycle
    componentWillMount() {
        const { id } = this.props.params;
        if (!id) return this.props.router.goBack();
        this.props.booking_actions && this.props.booking_actions.fetch_one(id);
        this.props.accounting_actions && this.props.accounting_actions.fetch(id);
        this.props.properties_actions && this.props.properties_actions.fetch_one(this.state.IdProperty);
    }
    componentWillReceiveProps = nextProps => {
        const { book } = nextProps;
        if (book?.IdBook && book.LogoUrl && !this.logoRequested) {
            this.logoRequested = true;
            this.loadLogo(`${book.LogoUrl}?full=100`);
        }
    }
    //

    // El logo se incrusta como data URL para que html2canvas lo pueda pintar en el PDF
    loadLogo = (url) => {
        fetch(url)
            .then(r => r.blob())
            .then(blob => new Promise(resolve => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            }))
            .then(logo => this.setState({ logo }))
            .catch(() => this.setState({ logo: url }));
    }

    // html2pdf (jsPDF + html2canvas) se sirve desde public/vendor: webpack 4 no puede parsear el bundle.
    // Se carga sólo la primera vez que se descarga un PDF.
    loadHtml2pdf = () => {
        if (window.html2pdf) return Promise.resolve(window.html2pdf);
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `${process.env.PUBLIC_URL || ''}/vendor/html2pdf-0.14.0.bundle.min.js`;
            script.onload = () => resolve(window.html2pdf);
            script.onerror = reject;
            document.body.appendChild(script);
        });
    }

    onDownload = () => {
        const { book } = this.props;
        this.setState({ downloading: true });
        this.loadHtml2pdf().then(html2pdf => {
            return html2pdf().set({
                margin: 0,
                filename: `Prefactura-${padWithZeros(book.PNR)}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
                jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
                pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' },
            }).from(this.sheet).save();
        }).catch(e => console.error('[prefactura] No se pudo generar el PDF', e))
            .finally(() => this.setState({ downloading: false }));
    }

    money = (v) => formatCurrency(v, this.state.currency, 'es-VE');
    rate = (a) => this.state.currency === 'USD' ? 1 : (a.ConversionBS ?? 1);

    renderToolbar = () => {
        const { currency, downloading } = this.state;
        return <div className="pf-toolbar">
            <span style={{ color: '#fff', fontSize: 13, marginRight: 'auto' }}>Vista previa · Prefactura</span>
            <button style={{ background: '#4a4f54', color: '#fff' }}
                onClick={() => this.setState({ currency: currency === 'USD' ? 'VEF' : 'USD' })}>
                <i className="fas fa-exchange-alt me-2" />Ver en {currency === 'USD' ? 'Bs.' : 'USD'}
            </button>
            <button style={{ background: '#4a4f54', color: '#fff' }} onClick={() => window.print()}>
                <i className="fas fa-print me-2" />Imprimir
            </button>
            <button style={{ background: C.accent, color: '#fff' }} disabled={downloading} onClick={this.onDownload}>
                <i className={`fas fa-${downloading ? 'spinner fa-spin' : 'file-pdf'} me-2`} />{downloading ? 'Generando...' : 'Descargar PDF'}
            </button>
        </div>
    }

    renderClient = () => {
        const { book } = this.props;
        const isGroup = book.IsGroup === 1,
            guest = book.Guest || {},
            coorp = book.Coorp || {};

        const client = isGroup ? {
            name: coorp.LegalName || '-',
            id: coorp.ID || '-',
            address: coorp.Address || '-',
            phone: coorp.Phone ? `${coorp.PhoneCountryCode || ''} ${coorp.Phone}` : '-',
            email: coorp.Email || '-',
        } : {
            name: `${guest.FirstName || ''} ${guest.LastName || ''}`.trim() || '-',
            id: guest.IdOrPassport || '-',
            address: guest.Address || book.Address || '-',
            phone: guest.Cellphone ? `${guest.CellPhoneCountryCode || ''} ${guest.Cellphone}` : '-',
            email: guest.Email || '-',
        };

        const nights = Math.max(moment(book.CheckOut).utc().startOf('day').diff(moment(book.CheckIn).utc().startOf('day'), 'days'), 1),
            pieces = _.chain(book.Rooms).map(r => r.PieceLabel ? `${r.PieceLabel}${r.GivenName ? ` (${r.GivenName})` : ''}` : r.GivenName).compact().join(', ').value(),
            adults = _.sumBy(book.Rooms, r => r.Adults || 0),
            children = _.sumBy(book.Rooms, r => (r.Children || 0) + (r.Cribs || 0));

        const Field = ({ label, children, style }) => <div style={{ marginBottom: 6, ...style }}>
            <div className="pf-label">{label}</div>
            <div>{children}</div>
        </div>;

        return <div style={{ display: 'flex', gap: 16, marginTop: 18 }}>
            <div style={{ flex: 1.2, border: `1px solid ${C.line}`, borderRadius: 4, padding: '10px 12px' }}>
                <div className="pf-label" style={{ color: C.accent, fontWeight: 700, marginBottom: 8 }}>{isGroup ? 'Empresa' : 'Cliente'}</div>
                <Field label={isGroup ? 'Razón social' : 'Nombre'}><b>{client.name}</b></Field>
                <div style={{ display: 'flex', gap: 12 }}>
                    <Field label={isGroup ? 'RIF' : 'Cédula / Pasaporte'} style={{ flex: 1 }}>{client.id}</Field>
                    <Field label="Teléfono" style={{ flex: 1 }}>{client.phone}</Field>
                </div>
                <Field label="Email">{client.email}</Field>
                <Field label="Dirección" style={{ marginBottom: 0 }}>{client.address}</Field>
            </div>
            <div style={{ flex: 1, border: `1px solid ${C.line}`, borderRadius: 4, padding: '10px 12px' }}>
                <div className="pf-label" style={{ color: C.accent, fontWeight: 700, marginBottom: 8 }}>Estadía</div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <Field label="Check-in" style={{ flex: 1 }}>{moment(book.CheckIn).utc().format('DD/MM/YYYY')}</Field>
                    <Field label="Check-out" style={{ flex: 1 }}>{moment(book.CheckOut).utc().format('DD/MM/YYYY')}</Field>
                    <Field label="Noches" style={{ flex: .6 }}>{nights}</Field>
                </div>
                <Field label="Habitaciones">{pieces || '-'}</Field>
                <div style={{ display: 'flex', gap: 12 }}>
                    <Field label="Adultos / Niños" style={{ flex: 1, marginBottom: 0 }}>{adults} / {children}</Field>
                    <Field label="Tarifa" style={{ flex: 1, marginBottom: 0 }}>{book.RateHeaderName || '-'}</Field>
                </div>
            </div>
        </div>
    }

    render() {
        const { book, property } = this.props, accounting = this.props.accounting || [];
        const { currency, issued, logo, hotel_name } = this.state;
        const { payment_types } = config;
        const ready = book?.IdBook && book.IdBook === this.props.params.id && property;

        if (!ready) return <section className="pf-backdrop">
            <style>{css}</style>
            <div style={{ color: '#fff', textAlign: 'center', marginTop: 120, fontSize: 14 }}>
                <i className="fas fa-spinner fa-spin me-2" />Preparando prefactura...
            </div>
        </section>;

        const TAX = property.Tax || 0;
        const charges = _.chain(accounting).filter(a => parseFloat(a.Debit) > 0).sortBy('CreateDate').value(),
            payments = _.chain(accounting).filter(a => parseFloat(a.Credit) > 0).sortBy('CreateDate').value();

        // Mismo cálculo que la Cuenta Maestra
        const SUB_TOTAL = _.sumBy(accounting, a => parseFloat(a.Debit || 0) * this.rate(a)),
            TAXABLE = _.chain(accounting).reject(a => a.IsTaxFree).sumBy(a => parseFloat(a.Debit || 0) * this.rate(a)).value(),
            EXEMPT = SUB_TOTAL - TAXABLE,
            TAXES = TAXABLE * TAX / 100,
            TOTAL = SUB_TOTAL + TAXES,
            PAID = _.sumBy(accounting, a => parseFloat(a.Credit || 0) * this.rate(a)),
            BALANCE = TOTAL - PAID;

        const user = fakeAuth.getUser() || {};

        return <section className="pf-backdrop">
            <style>{css}</style>
            {this.renderToolbar()}

            <div className="pf-sheet" ref={i => this.sheet = i}>

                {/* Encabezado */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, paddingBottom: 14, borderBottom: `3px solid ${C.accent}` }}>
                    <div style={{ flex: 1, display: 'flex', gap: 14, alignItems: 'center' }}>
                        {logo && <img src={logo} alt="" style={{ maxHeight: 72, maxWidth: 150, objectFit: 'contain' }} />}
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 700 }}>{property.Name || hotel_name}</div>
                            {property.RIF && <div>RIF: {property.RIF}</div>}
                            {property.Phone && <div>Tel: {property.PhoneCountryCode || ''} {property.Phone}</div>}
                            {property.Website && <div style={{ color: C.muted }}>{property.Website}</div>}
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '.08em', color: C.accent }}>PREFACTURA</div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>Reserva N° {padWithZeros(book.PNR)}</div>
                        <div style={{ color: C.muted }}>Emitida: {issued.format('DD/MM/YYYY hh:mm A')}</div>
                        <div style={{ color: C.muted }}>Moneda: {currency === 'USD' ? 'Dólares (USD)' : 'Bolívares (Bs.)'}</div>
                    </div>
                </div>

                {this.renderClient()}

                {/* Cargos */}
                <div className="pf-section">Detalle de consumos</div>
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: 80 }}>Fecha</th>
                            <th>Concepto</th>
                            <th className="pf-num" style={{ width: 110 }}>Monto</th>
                        </tr>
                    </thead>
                    <tbody>
                        {charges.map(a => <tr key={a.IdBookAccounting}>
                            <td>{moment(a.CreateDate).format('DD/MM/YYYY')}</td>
                            <td>
                                {a.Reason || '-'}
                                {a.IsTaxFree ? <span style={{ color: C.muted }}> (E)</span> : null}
                                {a.IsRefund ? <span style={{ color: C.muted }}> · Reembolso</span> : null}
                                {a.TransferFromIdBook && <div style={{ color: C.muted, fontSize: 9 }}>Transferido desde Reserva #{a.TransferFromPNR} · {a.TransferFromLabel}</div>}
                            </td>
                            <td className="pf-num">{this.money(parseFloat(a.Debit) * this.rate(a))}</td>
                        </tr>)}
                        {charges.length === 0 && <tr><td colSpan={3} style={{ color: C.muted, textAlign: 'center', padding: 14 }}>No hay consumos registrados</td></tr>}
                    </tbody>
                </table>

                {/* Totales */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <table style={{ width: '48%' }}>
                        <tbody>
                            <tr><td>Base imponible</td><td className="pf-num">{this.money(TAXABLE)}</td></tr>
                            {EXEMPT !== 0 && <tr><td>Exento / No sujeto (E)</td><td className="pf-num">{this.money(EXEMPT)}</td></tr>}
                            <tr><td>IVA ({TAX}%)</td><td className="pf-num">{this.money(TAXES)}</td></tr>
                            <tr style={{ fontWeight: 700, fontSize: 12 }}>
                                <td style={{ borderBottom: `1.5px solid ${C.ink}` }}>Total</td>
                                <td className="pf-num" style={{ borderBottom: `1.5px solid ${C.ink}` }}>{this.money(TOTAL)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Pagos */}
                <div className="pf-section">Pagos recibidos</div>
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: 80 }}>Fecha</th>
                            <th>Forma de pago</th>
                            <th>Referencia</th>
                            <th className="pf-num" style={{ width: 110 }}>Monto</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.map(a => <tr key={a.IdBookAccounting}>
                            <td>{moment(a.CreateDate).format('DD/MM/YYYY')}</td>
                            <td>{(_.find(payment_types, pt => pt.id === a.IdPaymentType) || _.first(payment_types)).name}</td>
                            <td>{a.Confirmation || '-'}</td>
                            <td className="pf-num">{this.money(parseFloat(a.Credit) * this.rate(a))}</td>
                        </tr>)}
                        {payments.length === 0 && <tr><td colSpan={4} style={{ color: C.muted, textAlign: 'center', padding: 14 }}>No se han registrado pagos</td></tr>}
                    </tbody>
                </table>

                {/* Resumen */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <div style={{ width: '48%', border: `1.5px solid ${C.ink}`, borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px' }}>
                            <span>Total</span><span className="pf-num">{this.money(TOTAL)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderTop: `1px solid ${C.line}` }}>
                            <span>Pagado</span><span className="pf-num">- {this.money(PAID)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: C.soft, borderTop: `1.5px solid ${C.ink}`, fontSize: 13, fontWeight: 800, color: BALANCE > 0.005 ? C.red : C.green }}>
                            <span>{BALANCE < -0.005 ? 'Saldo a favor' : 'Saldo pendiente'}</span>
                            <span className="pf-num">{this.money(Math.abs(BALANCE))}</span>
                        </div>
                    </div>
                </div>

                {/* Notas */}
                <div style={{ marginTop: 28, paddingTop: 10, borderTop: `1px solid ${C.line}`, color: C.muted, fontSize: 9 }}>
                    <div>(E) Exento o no sujeto a IVA.</div>
                    {currency !== 'USD' && <div>Montos en bolívares calculados a la tasa de cambio vigente en la fecha de cada transacción.</div>}
                    <div style={{ marginTop: 4, fontWeight: 700, color: C.ink }}>Este documento es una prefactura informativa y no constituye una factura fiscal.</div>
                    <div style={{ marginTop: 4 }}>Generado por {`${user.FirstName || ''} ${user.LastName || ''}`.trim() || 'Legendbeds'} · Legendbeds PMS</div>
                </div>
            </div>
        </section>
    }
}

export default PrintablePrefactura;
